// Canvas Resolver — single, config-driven capability port for canvas resolution.
//
// Transport is BffAdapter (injected at construction). Path templates and
// response field paths are pure config — no product-line-specific code.
//
// Defaults match the ARKS ui-artifacts envelope so existing product lines
// that omit `canvas` config keep working unchanged.

import type { CanvasSpec } from '../../features/Canvas/types';
import type { BffAdapter } from '../bff/BffAdapter';

// ── Port ──────────────────────────────────────────────────────────────

export type CanvasListScope =
  | { kind: 'solution'; solutionUkey: string }
  | { kind: 'module'; moduleUkey: string }
  | { kind: 'workspace'; workspaceUkey: string };

export interface CanvasListItem {
  ukey: string;
  spec: CanvasSpec;
}

export interface CanvasResolver {
  readonly name: string;
  fetchSpec(ukey: string, opts?: { signal?: AbortSignal }): Promise<CanvasSpec>;
  listRootCanvases(scope: CanvasListScope): Promise<CanvasListItem[]>;
}

// ── Config ────────────────────────────────────────────────────────────

export interface CanvasResolverConfig {
  /** Path template for fetching a single leaf canvas. `{key}` is replaced with the ukey.
   *  Default: `/services/registry/ui-artifacts/{key}` */
  specPath?: string;

  /**
   * Optional path template for module / root keys (shorter ukeys).
   * When set, ukeys with ≤ `moduleKeyMaxSegments` hyphen segments use this path
   * instead of `specPath` (e.g. `mod-root` → module, `mod-root-dashboard` → canvas).
   */
  modulePath?: string;

  /**
   * Max hyphen-separated segments treated as a module key when `modulePath` is set.
   * Default 2 (`mod-root` is a module; `mod-root-dashboard` is a canvas).
   */
  moduleKeyMaxSegments?: number;

  /** Path template for listing root canvases. `{scope}` is replaced with the scope key.
   *  Default: `/services/registry/ui-artifacts?solution_ukey={scope}&artifact_kind=canvas_root` */
  listPath?: string;

  /** Dot-path to `CanvasSpec` in the fetch response. Default: `response.payload` */
  specField?: string;

  /** Dot-path to items array in the list response. Default: `response.items` */
  listField?: string;

  /** Dot-path to `CanvasSpec` within each list item. Default: `payload` */
  listItemSpecField?: string;

  /** Dot-path to ukey within each list item. Default: `ukey` */
  listItemUkeyField?: string;
}

// ── Error types ───────────────────────────────────────────────────────

export class CanvasResolverError extends Error {
  constructor(message: string, readonly code: string) {
    super(message);
    this.name = 'CanvasResolverError';
  }
}

export class CanvasNotFound extends CanvasResolverError {
  constructor(ukey: string) {
    super(`Canvas '${ukey}' not found`, 'CANVAS_NOT_FOUND');
    this.name = 'CanvasNotFound';
  }
}

// ── Dot-path extraction utility ───────────────────────────────────────

function getByDotPath(obj: unknown, dotPath: string): unknown {
  return dotPath.split('.').reduce(
    (current, key) => (current != null && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined),
    obj
  );
}

// ── Defaults (ARKS ui-artifacts envelope) ─────────────────────────────

const DEFAULTS = {
  specPath: '/services/registry/ui-artifacts/{key}',
  listPath: '/services/registry/ui-artifacts?solution_ukey={scope}&artifact_kind=canvas_root',
  specField: 'response.payload',
  listField: 'response.items',
  listItemSpecField: 'payload',
  listItemUkeyField: 'ukey',
} as const;

// ── BffCanvasResolver ─────────────────────────────────────────────────

export class BffCanvasResolver implements CanvasResolver {
  readonly name = 'bff-canvas-resolver';

  private readonly specPath: string;
  private readonly modulePath: string | null;
  private readonly moduleKeyMaxSegments: number;
  private readonly listPath: string;
  private readonly specField: string;
  private readonly listField: string;
  private readonly listItemSpecField: string;
  private readonly listItemUkeyField: string;

  constructor(private readonly bff: BffAdapter, cfg: CanvasResolverConfig = {}) {
    this.specPath = cfg.specPath ?? DEFAULTS.specPath;
    this.modulePath = cfg.modulePath?.trim() ? cfg.modulePath : null;
    this.moduleKeyMaxSegments = cfg.moduleKeyMaxSegments ?? 2;
    this.listPath = cfg.listPath ?? DEFAULTS.listPath;
    this.specField = cfg.specField ?? DEFAULTS.specField;
    this.listField = cfg.listField ?? DEFAULTS.listField;
    this.listItemSpecField = cfg.listItemSpecField ?? DEFAULTS.listItemSpecField;
    this.listItemUkeyField = cfg.listItemUkeyField ?? DEFAULTS.listItemUkeyField;
  }

  private resolveFetchPath(ukey: string): string {
    const useModule =
      this.modulePath != null &&
      ukey.split('-').filter(Boolean).length <= this.moduleKeyMaxSegments;
    const template = useModule ? this.modulePath! : this.specPath;
    return template.replace('{key}', encodeURIComponent(ukey));
  }

  async fetchSpec(ukey: string, opts?: { signal?: AbortSignal }): Promise<CanvasSpec> {
    const path = this.resolveFetchPath(ukey);
    const raw = await this.bff.request<unknown>(path, { signal: opts?.signal });
    const spec = getByDotPath(raw, this.specField);
    if (!spec) throw new CanvasNotFound(ukey);
    return spec as CanvasSpec;
  }

  async listRootCanvases(scope: CanvasListScope): Promise<CanvasListItem[]> {
    const scopeKey = scope.kind === 'solution' ? scope.solutionUkey
      : scope.kind === 'module' ? scope.moduleUkey
      : scope.workspaceUkey;

    const path = this.listPath.replace('{scope}', encodeURIComponent(scopeKey));
    const raw = await this.bff.request<unknown>(path);
    const items = getByDotPath(raw, this.listField);
    if (!Array.isArray(items)) return [];

    return items
      .map(item => ({
        ukey: getByDotPath(item, this.listItemUkeyField) as string,
        spec: getByDotPath(item, this.listItemSpecField) as CanvasSpec,
      }))
      .filter(item => item.ukey != null && item.spec != null);
  }
}
